import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeacher } from '@/context/TeacherContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { cn, getButtonColor } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { FileText, Edit2, Trash2, Eye, Download, ArrowLeft, Save, X, Bold, Italic, Upload, Loader2, AlertTriangle, Search, Plus, Check, XCircle, Layout, List, Sparkles } from 'lucide-react';
import TeacherNotePreviewModal from './TeacherNotePreviewModal';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkLatexSanitizer from '@/utils/remarkLatexSanitizer';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { sanitizeChatMarkdown } from '@/utils/chatMarkdownSanitizer';
import {
  getTeacherNotes,
  createTeacherNote,
  updateTeacherNote,
  deleteTeacherNote,
  uploadNoteFile,
  getNoteStatus,
  getNoteDetail,
  repairTeacherNoteMath,
} from '@/api/teacherNotes';
import { trackEvent, attachUserProps } from '@/analytics/analytics';

export default function TeacherNotes() {
  const navigate = useNavigate();
  const { teacherTheme } = useTeacher();
  const { toast } = useToast();
  const isDark = teacherTheme === 'dark';

  // API Data States
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [savedNotes, setSavedNotes] = useState([]);
  const [loading, setLoading] = useState({ notes: false, classes: false, subjects: false });

  // Form States
  const [selectedDivisionId, setSelectedDivisionId] = useState(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [selectedChapterId, setSelectedChapterId] = useState(null);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [visibleToStudents, setVisibleToStudents] = useState(true);
  const [allowDownload, setAllowDownload] = useState(true);
  const [activeTab, setActiveTab] = useState('create');
  const [createSubTab, setCreateSubTab] = useState('file'); // 'file' for AI-OCR, 'manual' for Manual Notes
  const [selectedNoteToEdit, setSelectedNoteToEdit] = useState(null);
  const [originalNoteTitle, setOriginalNoteTitle] = useState('');
  const [originalNoteContent, setOriginalNoteContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // File Upload States
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processingNoteId, setProcessingNoteId] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const pollingIntervalRef = useRef(null);
  const fileInputRef = useRef(null);

  // Preview State
  const [selectedNote, setSelectedNote] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [repairingMath, setRepairingMath] = useState(false);

  // View Note Modal State
  const [viewingNote, setViewingNote] = useState(null);

  const handleRepairMathForSelectedNote = async () => {
    if (!selectedNote?.id) return;
    setRepairingMath(true);
    try {
      const updated = await repairTeacherNoteMath(selectedNote.id);

      setSavedNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
      setSelectedNote(updated);

      toast({
        title: 'Equations updated',
        description: 'Math content was re-processed and saved.',
        duration: 4000,
      });
    } catch (e) {
      console.error('Failed to repair math', e);
      toast({
        title: 'Could not repair equations',
        description: e?.response?.data?.error || e?.message || 'Please try again.',
        variant: 'destructive',
        duration: 6000,
      });
    } finally {
      setRepairingMath(false);
    }
  };

  // Assign Classes Modal State
  const [assignClassesNote, setAssignClassesNote] = useState(null);
  const [selectedDivisionIds, setSelectedDivisionIds] = useState([]);
  const [assigningClasses, setAssigningClasses] = useState(false);

  // Delete Confirmation Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  // TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setNoteContent(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: cn('focus:outline-none min-h-80 p-4 w-full', isDark ? 'bg-[hsl(var(--card-border))]/30 text-[hsl(var(--foreground))]' : 'bg-white text-[#2B2B2B]'),
      },
    },
  });

  // Fetch classes (divisions)
  useEffect(() => {
    const fetchClasses = async () => {
      setLoading(prev => ({ ...prev, classes: true }));
      try {
        const token = getAuthToken('teacher');
        if (!token) return;

        const res = await axios.get(apiUrl('v1/teachers/classes/'), {
          headers: { Authorization: `Bearer ${token}` }
        });

        const apiClasses = res?.data?.classes || [];
        setClasses(apiClasses);

        // No need to set default division - divisions will be assigned later from saved notes card
      } catch (e) {
        console.error('Failed to fetch classes', e);
      } finally {
        setLoading(prev => ({ ...prev, classes: false }));
      }
    };

    fetchClasses();
  }, []);

  // Fetch subjects and chapters
  useEffect(() => {
    const fetchSubjectsChapters = async () => {
      setLoading(prev => ({ ...prev, subjects: true }));
      try {
        const token = getAuthToken('teacher');
        if (!token) return;

        const res = await axios.get(apiUrl('v1/teachers/ai-tools/subjects-chapters/'), {
          headers: { Authorization: `Bearer ${token}` }
        });

        const apiSubjects = res?.data?.subjects || [];
        setSubjects(apiSubjects);

        // Set default selection
        if (apiSubjects.length > 0 && !selectedSubjectId) {
          setSelectedSubjectId(apiSubjects[0].subject_id);
          if (apiSubjects[0].chapters.length > 0) {
            setSelectedChapterId(apiSubjects[0].chapters[0].chapter_id);
          }
        }
      } catch (e) {
        console.error('Failed to fetch subjects/chapters', e);
      } finally {
        setLoading(prev => ({ ...prev, subjects: false }));
      }
    };

    fetchSubjectsChapters();
  }, []);

  // Update chapters when subject changes
  useEffect(() => {
    if (selectedSubjectId && subjects.length > 0) {
      const subject = subjects.find(s => s.subject_id === parseInt(selectedSubjectId));
      if (subject && subject.chapters.length > 0) {
        setChapters(subject.chapters);
        if (!selectedChapterId || !subject.chapters.find(c => c.chapter_id === parseInt(selectedChapterId))) {
          setSelectedChapterId(subject.chapters[0].chapter_id);
        }
      } else {
        setChapters([]);
        setSelectedChapterId(null);
      }
    }
  }, [selectedSubjectId, subjects]);

  // Fetch notes
  useEffect(() => {
    const fetchNotes = async () => {
      setLoading(prev => ({ ...prev, notes: true }));
      try {
        const data = await getTeacherNotes();
        setSavedNotes(data.notes || []);
      } catch (e) {
        console.error('Failed to fetch notes', e);
        setSavedNotes([]);
      } finally {
        setLoading(prev => ({ ...prev, notes: false }));
      }
    };

    fetchNotes();
  }, []);

  // Poll for processing status and refresh notes list
  useEffect(() => {
    if (!processingNoteId) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    const pollStatus = async () => {
      try {
        const status = await getNoteStatus(processingNoteId);
        setProcessingStatus(status);

        // Refresh notes list to update processing status in cards
        const data = await getTeacherNotes();
        setSavedNotes(data.notes || []);

        // If processing is complete, show toast and stop polling
        if (status.processing_status === 'correction_complete') {
          setProcessingNoteId(null);
          setProcessingStatus(null);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          // Show completion toast
          toast({
            title: "Processing complete!",
            description: "Your note is ready for review. Click 'Review and Save' to review and finalize the content.",
            duration: 7000,
          });
          return;
        }

        // If failed, stop polling
        if (status.processing_status === 'failed') {
          setProcessingNoteId(null);
          setProcessingStatus(null);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return;
        }

        // If OCR complete, load note detail to get content (for edit mode only)
        if (status.processing_status === 'ocr_complete' && status.has_ocr_text && selectedNoteToEdit?.id === processingNoteId) {
          const noteDetail = await getNoteDetail(processingNoteId);
          if (noteDetail.content && editor) {
            // Content is already in HTML format from Vision API
            editor.commands.setContent(noteDetail.content);
            setNoteContent(noteDetail.content);
          }
        }

        // If correction complete, load note detail to get rich text content
        if (status.processing_status === 'correction_complete' && status.has_corrected_text) {
          const noteDetail = await getNoteDetail(processingNoteId);
          // Use content field which now contains rich text HTML from Vision API
          const htmlContent = noteDetail.content || '';
          if (htmlContent && editor) {
            // Content is already in HTML format, so use directly
            editor.commands.setContent(htmlContent);
            setNoteContent(htmlContent);
          }
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }

        // If failed, stop polling
        if (status.processing_status === 'failed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      } catch (e) {
        console.error('Failed to poll status', e);
      }
    };

    // Poll immediately, then every 2-3 seconds
    pollStatus();
    pollingIntervalRef.current = setInterval(pollStatus, 2500);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [processingNoteId, editor]);

  // Update editor content when editing a note
  useEffect(() => {
    if (editor && selectedNoteToEdit) {
      const content = selectedNoteToEdit.content || '';
      editor.commands.setContent(content);
      setNoteContent(content);
    }
  }, [editor, selectedNoteToEdit]);

  const [updatingNote, setUpdatingNote] = useState(false);

  // Helper function to check if HTML content has actual text (strips HTML tags)
  const hasTextContent = (htmlContent) => {
    if (!htmlContent || !htmlContent.trim()) return false;
    // Create a temporary DOM element to strip HTML tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    return textContent.trim().length > 0;
  };

  const stripHtmlToText = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    return text;
  };

  const buildStructuredMarkdown = (structuredContent) => {
    if (!structuredContent || !Array.isArray(structuredContent.sections) || structuredContent.sections.length === 0) {
      return '';
    }

    const lines = [];
    for (const section of structuredContent.sections) {
      if (section?.heading) {
        lines.push(`### ${section.heading}`);
      }
      if (Array.isArray(section?.points)) {
        for (const point of section.points) {
          if (point && String(point).trim()) {
            lines.push(`- ${String(point).trim()}`);
          }
        }
      }
      lines.push('');
    }
    return lines.join('\n').trim();
  };

  const getNoteMarkdownForDisplay = (note) => {
    const structuredMd = buildStructuredMarkdown(note?.structured_content);
    if (structuredMd) return structuredMd;

    // Fallback: strip HTML so math delimiters ($...$, $$...$$) remain as plain text
    const text = stripHtmlToText(note?.content || '');
    return text || '';
  };

  // Helper function to check if Save/Update button should be enabled
  const isSaveButtonEnabled = () => {
    if (selectedNoteToEdit) {
      // For editing: enable if title or content has changed
      const titleChanged = noteTitle.trim() !== originalNoteTitle.trim();
      const contentChanged = noteContent.trim() !== originalNoteContent.trim();
      return titleChanged || contentChanged;
    } else {
      // For new manual note: enable if title OR content has text
      const hasTitle = noteTitle.trim().length > 0;
      const hasContent = hasTextContent(noteContent);
      return createSubTab === 'manual' && (hasTitle || hasContent);
    }
  };

  const handleSaveNote = async () => {
    // Validate only for manual notes (content should exist)
    // For AI-OCR notes, content might be empty initially
    if (createSubTab === 'manual' && (!noteTitle.trim() || !noteContent.trim())) {
      toast({
        title: "Validation error",
        description: "Please fill in title and content",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    if (!selectedSubjectId || !selectedChapterId) {
      toast({
        title: "Validation error",
        description: "Please select subject and chapter",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    try {
      if (selectedNoteToEdit) {
        // Update existing note
        setUpdatingNote(true);
        await updateTeacherNote(selectedNoteToEdit.id, {
          title: noteTitle,
          content: noteContent,
          visible_to_students: visibleToStudents,
          allow_download: allowDownload,
        });

        // Navigate to saved notes immediately
        setActiveTab('saved');

        // Show toast
        const isOCRNote = selectedNoteToEdit.file_upload || selectedNoteToEdit.file_path;
        toast({
          title: isOCRNote ? "Note reviewed and saved!" : "Note updated!",
          description: isOCRNote ? "Embeddings are being created in the background. You'll be notified when complete." : "Embeddings are being created in the background. You'll be notified when complete.",
          duration: 4000,
        });

        // Clear form
        setSelectedNoteToEdit(null);
        setOriginalNoteTitle('');
        setOriginalNoteContent('');
        setNoteTitle('');
        setNoteContent('');
        setVisibleToStudents(true);
        setAllowDownload(true);
        setCreateSubTab('file'); // Reset to AI-OCR tab
        if (editor) {
          editor.commands.setContent('');
        }

        // Refresh notes list to show updated status
        const data = await getTeacherNotes();
        setSavedNotes(data.notes || []);

        // Start polling for embedding completion
        const noteId = selectedNoteToEdit.id;
        const pollInterval = setInterval(async () => {
          try {
            const noteDetail = await getNoteDetail(noteId);
            if (noteDetail.embedding_status === 'complete' || noteDetail.embedding_status === 'failed') {
              clearInterval(pollInterval);
              setUpdatingNote(false);

              // Refresh notes list
              const updatedData = await getTeacherNotes();
              setSavedNotes(updatedData.notes || []);

              // Show completion toast
              if (noteDetail.embedding_status === 'complete') {
                toast({
                  title: "Embeddings created!",
                  description: "Your note is now ready. You can assign it to classes.",
                  duration: 4000,
                });
              } else {
                toast({
                  title: "Embedding failed",
                  description: "Failed to create embeddings. Please try updating the note again.",
                  variant: "destructive",
                  duration: 4000,
                });
              }
            }
          } catch (error) {
            console.error('Error polling embedding status:', error);
          }
        }, 2000); // Poll every 2 seconds

        // Stop polling after 60 seconds max
        setTimeout(() => {
          clearInterval(pollInterval);
          setUpdatingNote(false);
        }, 60000);
        try { trackEvent('teacher_note_updated', attachUserProps({ note_id: selectedNoteToEdit.id, chapter_id: parseInt(selectedChapterId) })); } catch (e) { }
      } else {
        // Create new manual note (without divisions - will be assigned later from saved notes card)
        await createTeacherNote({
          division_ids: [], // Empty array - divisions will be assigned later via "Assign Classes" button
          chapter_id: parseInt(selectedChapterId),
          title: noteTitle,
          content: noteContent,
          visible_to_students: visibleToStudents,
          allow_download: allowDownload,
        });

        // Navigate to saved notes immediately
        setActiveTab('saved');

        toast({
          title: "Note created!",
          description: "Embeddings are being created in the background. You'll be notified when complete.",
          duration: 4000,
        });

        // Clear form
        setNoteTitle('');
        setNoteContent('');
        setOriginalNoteTitle('');
        setOriginalNoteContent('');
        setVisibleToStudents(true);
        setAllowDownload(true);
        setSelectedFile(null);
        setProcessingNoteId(null);
        setProcessingStatus(null);
        setSelectedNoteToEdit(null);
        setCreateSubTab('file'); // Reset to AI-OCR tab

        if (editor) {
          editor.commands.setContent('');
        }

        // Refresh notes list
        const data = await getTeacherNotes();
        setSavedNotes(data.notes || []);

        // Start polling for embedding completion
        const newNoteId = data.notes?.find(n => n.title === noteTitle && n.chapter?.id === parseInt(selectedChapterId))?.id;
        if (newNoteId) {
          const pollInterval = setInterval(async () => {
            try {
              const noteDetail = await getNoteDetail(newNoteId);
              if (noteDetail.embedding_status === 'complete' || noteDetail.embedding_status === 'failed') {
                clearInterval(pollInterval);

                // Refresh notes list
                const updatedData = await getTeacherNotes();
                setSavedNotes(updatedData.notes || []);

                // Show completion toast
                if (noteDetail.embedding_status === 'complete') {
                  toast({
                    title: "Embeddings created!",
                    description: "Your note is now ready. You can assign it to classes.",
                    duration: 4000,
                  });
                } else {
                  toast({
                    title: "Embedding failed",
                    description: "Failed to create embeddings. Please try updating the note again.",
                    variant: "destructive",
                    duration: 4000,
                  });
                }
              }
            } catch (error) {
              console.error('Error polling embedding status:', error);
            }
          }, 2000); // Poll every 2 seconds

          // Stop polling after 60 seconds max
          setTimeout(() => {
            clearInterval(pollInterval);
          }, 60000);
          try { trackEvent('teacher_note_created', attachUserProps({ chapter_id: parseInt(selectedChapterId) })); } catch (e) { }
        }
      }
    } catch (e) {
      console.error('Failed to save note', e);
      alert('Failed to save note: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleEditNote = async (note, isReviewMode = false) => {
    try {
      // Fetch full note detail
      const noteDetail = await getNoteDetail(note.id);

      setSelectedNoteToEdit(noteDetail);
      const title = noteDetail.title || '';
      const content = noteDetail.content || '';

      // Store original values for change detection
      setOriginalNoteTitle(title);
      setOriginalNoteContent(content);

      setNoteTitle(title);
      // For review mode (completed OCR), we don't need to set division/subject/chapter
      // as they're already set and shouldn't be changed
      if (!isReviewMode) {
        setSelectedDivisionId(noteDetail.divisions?.[0]?.id);
        setSelectedSubjectId(noteDetail.subject?.id);
        setSelectedChapterId(noteDetail.chapter?.id);
      }
      setVisibleToStudents(noteDetail.visible_to_students);
      setAllowDownload(noteDetail.allow_download);

      // Set content
      if (editor) {
        editor.commands.setContent(content);
      }
      setNoteContent(content);

      // If note is still processing, start polling
      if (noteDetail.processing_status && ['pending', 'ocr_processing', 'correction_processing'].includes(noteDetail.processing_status)) {
        setProcessingNoteId(noteDetail.id);
        setProcessingStatus({
          processing_status: noteDetail.processing_status,
          has_ocr_text: !!noteDetail.content,  // Backward compatibility
          has_corrected_text: !!noteDetail.content,  // Backward compatibility
        });
      }

      setActiveTab('create');

      try { trackEvent('teacher_open_edit_note', attachUserProps({ note_id: note.id, review_mode: !!isReviewMode })); } catch (e) { }

      // Show message for review mode
      if (isReviewMode) {
        toast({
          title: "Review your note",
          description: "Please review the AI-generated content, make any necessary edits, and click 'Save Note' to finalize.",
          duration: 6000,
        });
      }
    } catch (e) {
      console.error('Failed to load note', e);
      alert('Failed to load note: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDeleteNoteClick = (id) => {
    setNoteToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!noteToDelete) return;

    try {
      await deleteTeacherNote(noteToDelete);

      // Refresh notes list
      const data = await getTeacherNotes();
      setSavedNotes(data.notes || []);

      // If deleted note was being edited, reset form
      if (selectedNoteToEdit && selectedNoteToEdit.id === noteToDelete) {
        setSelectedNoteToEdit(null);
        setOriginalNoteTitle('');
        setOriginalNoteContent('');
        setNoteTitle('');
        setNoteContent('');
        if (editor) {
          editor.commands.setContent('');
        }
      }

      // Close dialog and reset
      setDeleteDialogOpen(false);
      setNoteToDelete(null);

      // Show success toast
      toast({
        title: "Note deleted",
        description: "The note has been successfully deleted.",
        duration: 3000,
      });
      try { trackEvent('teacher_note_deleted', attachUserProps({ note_id: noteToDelete })); } catch (e) { }
    } catch (e) {
      console.error('Failed to delete note', e);
      toast({
        title: "Delete failed",
        description: 'Failed to delete note: ' + (e.response?.data?.error || e.message),
        variant: "destructive",
        duration: 4000,
      });
      setDeleteDialogOpen(false);
      setNoteToDelete(null);
    }
  };

  const handleCancelEdit = () => {
    setSelectedNoteToEdit(null);
    setOriginalNoteTitle('');
    setOriginalNoteContent('');
    setNoteTitle('');
    setNoteContent('');
    setVisibleToStudents(true);
    setAllowDownload(true);
    setSelectedFile(null);
    setProcessingNoteId(null);
    setProcessingStatus(null);
    setCreateSubTab('file'); // Reset to AI-OCR tab

    if (editor) {
      editor.commands.setContent('');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        // Reset file input and selected file state
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        toast({
          title: "Invalid file type",
          description: "Please upload PDF, JPG, or PNG files only.",
          variant: "destructive",
          duration: 4000,
        });
        return;
      }

      // Validate file size (10MB)
      const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSizeInBytes) {
        // Reset file input and selected file state
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        toast({
          title: "File size exceeded",
          description: `File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds 10MB limit. Please upload a smaller file.`,
          variant: "destructive",
          duration: 5000,
        });
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file');
      return;
    }

    if (!selectedSubjectId || !selectedChapterId) {
      alert('Please select subject and chapter');
      return;
    }

    try {
      setUploading(true);

      // Upload file without division_ids - will be assigned later from saved notes card
      const note = await uploadNoteFile(
        selectedFile,
        [], // Empty division_ids - will be assigned later
        parseInt(selectedChapterId),
        noteTitle || selectedFile.name,
        visibleToStudents,
        allowDownload
      );

      // Start polling for processing status
      setProcessingNoteId(note.id);
      setProcessingStatus({
        processing_status: note.processing_status,
        has_ocr_text: false,
        has_corrected_text: false,
      });

      // Reset form
      setNoteTitle('');
      setNoteContent('');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (editor) {
        editor.commands.setContent('');
      }

      // Refresh notes list and switch to saved notes tab immediately
      const data = await getTeacherNotes();
      setSavedNotes(data.notes || []);

      setUploading(false);

      // Switch to saved notes tab to show processing status
      setActiveTab('saved');

      // Show informational toast
      toast({
        title: "File uploaded successfully",
        description: "Your note is being processed. You can track the progress in the Saved Notes tab.",
        duration: 5000,
      });
      try { trackEvent('teacher_note_file_uploaded', attachUserProps({ file_name: selectedFile?.name, chapter_id: parseInt(selectedChapterId) })); } catch (e) { }
    } catch (e) {
      console.error('Failed to upload file', e);
      alert('Failed to upload file: ' + (e.response?.data?.error || e.message));
      setUploading(false);
    }
  };

  const handleViewNote = async (note) => {
    try {
      // Fetch full note detail
      const noteDetail = await getNoteDetail(note.id);
      setViewingNote(noteDetail);
      try { trackEvent('teacher_view_note', attachUserProps({ note_id: note.id })); } catch (e) { }
    } catch (e) {
      console.error('Failed to load note', e);
      alert('Failed to load note: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleCloseViewNote = () => {
    setViewingNote(null);
  };

  const handleAssignClasses = async (note) => {
    try {
      // Fetch full note detail to get current divisions
      const noteDetail = await getNoteDetail(note.id);
      setAssignClassesNote(noteDetail);
      // Pre-select currently assigned division IDs
      const currentDivisionIds = (noteDetail.divisions || []).map(div => div.id);
      setSelectedDivisionIds(currentDivisionIds);
    } catch (e) {
      console.error('Failed to load note', e);
      alert('Failed to load note: ' + (e.response?.data?.error || e.message));
    }
    try { trackEvent('teacher_open_assign_note_dialog', attachUserProps({ note_id: note.id })); } catch (e) { }
  };

  const handleCloseAssignClasses = () => {
    setAssignClassesNote(null);
    setSelectedDivisionIds([]);
  };

  const handleToggleDivision = (divisionId) => {
    setSelectedDivisionIds(prev => {
      if (prev.includes(divisionId)) {
        return prev.filter(id => id !== divisionId);
      } else {
        return [...prev, divisionId];
      }
    });
  };

  // Render warning if no subjects assigned
  const renderAssignmentWarning = () => {
    if (!loading.subjects && subjects.length === 0) {
      return (
        <div className="mb-6 p-4 border border-yellow-200 bg-yellow-50 rounded-lg flex items-start gap-3">
          <div className="p-2 bg-yellow-100 rounded-full">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <h3 className="font-semibold text-yellow-900">No Subjects Assigned</h3>
            <p className="text-sm text-yellow-700 mt-1">
              It looks like you haven't been assigned any subjects or chapters yet.
              Please contact your school administrator to assign subjects to your account so you can create notes.
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const handleSaveAssignedClasses = async () => {
    if (!assignClassesNote) return;

    if (selectedDivisionIds.length === 0) {
      alert('Please select at least one class');
      return;
    }

    setAssigningClasses(true);
    try {
      // Ensure division_ids are integers
      const divisionIds = selectedDivisionIds.map(id => parseInt(id)).filter(id => !isNaN(id));

      await updateTeacherNote(assignClassesNote.id, {
        division_ids: divisionIds,
      });

      // Refresh notes list
      const data = await getTeacherNotes();
      setSavedNotes(data.notes || []);

      handleCloseAssignClasses();
      try { trackEvent('teacher_note_assigned', attachUserProps({ note_id: assignClassesNote.id, assigned_division_count: divisionIds.length })); } catch (e) { }
    } catch (e) {
      console.error('Failed to assign classes', e);
      alert('Failed to assign classes: ' + (e.response?.data?.error || e.message));
    } finally {
      setAssigningClasses(false);
    }
  };

  const filteredNotes = savedNotes.filter(note =>
    note.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.subject?.subject_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.chapter?.chapter_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected class, subject, chapter names for display
  const selectedClass = classes.find(c => c.id === selectedDivisionId);
  const selectedSubject = subjects.find(s => s.subject_id === parseInt(selectedSubjectId));
  const selectedChapter = chapters.find(c => c.chapter_id === parseInt(selectedChapterId));

  // Get processing status label
  const getProcessingStatusLabel = (status) => {
    const statusMap = {
      'pending': 'Pending',
      'ocr_processing': 'Processing OCR...',
      'ocr_complete': 'OCR Complete',
      'correction_processing': 'Processing...',
      'correction_complete': 'Complete',
      'failed': 'Failed',
    };
    return statusMap[status] || status;
  };

  // Check if note is currently processing (OCR or embedding)
  const isNoteProcessing = (note) => {
    // For OCR notes (with file_upload), check processing_status
    // For manual notes (without file_upload), processing_status may remain 'pending', so ignore it
    const hasFile = note.file_upload || note.file_path;
    const processingOCR = hasFile && note.processing_status && ['pending', 'ocr_processing', 'correction_processing'].includes(note.processing_status);
    const processingEmbedding = note.embedding_status && ['processing'].includes(note.embedding_status);
    return processingOCR || processingEmbedding;
  };

  // Convert rich HTML to plain text for previews (decodes entities like &nbsp;)
  const htmlToPlainText = (html) => {
    if (!html) return '';
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const text = temp.textContent || temp.innerText || '';
    return text.replace(/\s+/g, ' ').trim();
  };

  const truncateText = (text, maxLen = 100) => {
    if (!text) return '';
    if (text.length <= maxLen) return text;
    return text.slice(0, maxLen).trimEnd() + '...';
  };

  return (
    <div className={cn("flex flex-col h-full p-8 overflow-y-auto", isDark ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]")}>
      <div className="max-w-6xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/teacher/dashboard')}
              className={cn("rounded-xl", isDark ? "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" : "text-[#6B6B6B] hover:text-[#1A1A1A]")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={cn("text-3xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Class Notes</h1>
              <p className={cn("text-base font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Create and manage chapter notes for your students</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b" style={{ borderColor: isDark ? 'hsl(var(--card-border))' : '#E0D4BF' }}>
          <button
            onClick={() => setActiveTab('create')}
            className={cn(
              "px-4 py-2 rounded-lg font-medium text-sm transition-all h-9 shadow-sm",
              activeTab === 'create'
                ? getButtonColor(isDark)
                : isDark ? "bg-[hsl(var(--card-border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/80" : "bg-[#E0D4BF] text-[#1A1A1A] hover:bg-[#D4C4A8]"
            )}
          >
            Create / Edit Note
          </button>
          <button
            onClick={() => setActiveTab('saved')}
            className={cn(
              "px-4 py-2 rounded-lg font-medium text-sm transition-all h-9 shadow-sm",
              activeTab === 'saved'
                ? getButtonColor(isDark)
                : isDark ? "bg-[hsl(var(--card-border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/80" : "bg-[#E0D4BF] text-[#1A1A1A] hover:bg-[#D4C4A8]"
            )}
          >
            Saved Notes ({savedNotes.length})
          </button>
        </div>

        {/* CREATE/EDIT TAB */}
        {activeTab === 'create' && (
          <div className="space-y-6">
            {renderAssignmentWarning()}
            <Card className={cn("border shadow-sm", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#F5F1EA] to-[#EDE3D2] border-[#E0D4BF]")}>
              <CardHeader className={cn("border-b", isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}>
                <CardTitle className={cn("text-xl", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                  {selectedNoteToEdit ? 'Edit Note' : 'Create New Note'}
                </CardTitle>

                {/* Sub-tabs for Create mode only (not when editing) */}
                {!selectedNoteToEdit && (
                  <div className="flex gap-2 mt-4 border-b" style={{ borderColor: isDark ? 'hsl(var(--card-border))' : '#E0D4BF' }}>
                    <button
                      onClick={() => setCreateSubTab('file')}
                      className={cn(
                        "px-4 py-2 rounded-t-lg font-medium text-sm transition-all",
                        createSubTab === 'file'
                          ? getButtonColor(isDark)
                          : isDark ? "bg-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" : "bg-transparent text-[#6B6B6B] hover:text-[#2B2B2B]"
                      )}
                    >
                      AI-OCR
                    </button>
                    <button
                      onClick={() => setCreateSubTab('manual')}
                      className={cn(
                        "px-4 py-2 rounded-t-lg font-medium text-sm transition-all",
                        createSubTab === 'manual'
                          ? getButtonColor(isDark)
                          : isDark ? "bg-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" : "bg-transparent text-[#6B6B6B] hover:text-[#2B2B2B]"
                      )}
                    >
                      Manual Notes
                    </button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Processing Status */}
                {processingStatus && (
                  <div className={cn("p-4 rounded-lg border", isDark ? "bg-[hsl(var(--card-border))]/20 border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}>
                    <div className="flex items-center gap-2">
                      {processingStatus.processing_status === 'pending' && (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className={cn("text-sm", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                            Processing OCR...
                          </span>
                        </>
                      )}
                      {processingStatus.processing_status === 'ocr_processing' && (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className={cn("text-sm", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                            Extracting text from file...
                          </span>
                        </>
                      )}
                      {processingStatus.processing_status === 'ocr_complete' && (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className={cn("text-sm", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                            Grammar correction in progress...
                          </span>
                        </>
                      )}
                      {processingStatus.processing_status === 'correction_complete' && (
                        <>
                          <FileText className="w-4 h-4 text-green-600" />
                          <span className={cn("text-sm", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                            Processing complete! You can now edit the text.
                          </span>
                        </>
                      )}
                      {processingStatus.processing_status === 'failed' && (
                        <>
                          <X className="w-4 h-4 text-red-600" />
                          <span className={cn("text-sm text-red-600", isDark ? "text-red-400" : "text-red-600")}>
                            Processing failed. Please try uploading again.
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Subject and Chapter Selectors */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Subject</label>
                    <Select
                      value={selectedSubjectId?.toString()}
                      onValueChange={(val) => setSelectedSubjectId(val)}
                      disabled={loading.subjects}
                    >
                      <SelectTrigger className={cn("rounded-lg", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
                        <SelectValue placeholder="Select subject" />
                      </SelectTrigger>
                      <SelectContent className={cn(isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
                        {subjects.map(sub => (
                          <SelectItem key={sub.subject_id} value={sub.subject_id.toString()}>
                            {sub.subject_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Chapter</label>
                    <Select
                      value={selectedChapterId?.toString()}
                      onValueChange={(val) => setSelectedChapterId(val)}
                      disabled={loading.subjects || chapters.length === 0}
                    >
                      <SelectTrigger className={cn("rounded-lg", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
                        <SelectValue placeholder="Select chapter" />
                      </SelectTrigger>
                      <SelectContent className={cn(isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
                        {chapters.map(ch => (
                          <SelectItem key={ch.chapter_id} value={ch.chapter_id.toString()}>
                            {ch.chapter_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className={cn("text-xs", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                  Note: You can assign this note to specific classes (Grade-Division) later from the Saved Notes card.
                </p>

                {/* AI-OCR Tab Content */}
                {(!selectedNoteToEdit && createSubTab === 'file') && (
                  <div className={cn("border border-dashed rounded-lg p-6", isDark ? "border-[hsl(var(--card-border))] bg-[hsl(var(--card-border))]/10" : "border-[#E0D4BF] bg-[#F8F5F0]")}>
                    <div className="flex flex-col items-center justify-center gap-4">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="file-upload"
                        disabled={uploading}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="lg"
                        disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}
                        className={cn("cursor-pointer", getButtonColor(isDark))}
                      >
                        <Upload className={cn("w-5 h-5 mr-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")} />
                        {selectedFile ? (
                          <span className="truncate max-w-[200px]">{selectedFile.name}</span>
                        ) : (
                          'Select File'
                        )}
                      </Button>
                      <div className={cn("text-xs text-center", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                        <p>Upload PDF or Image for OCR Processing</p>
                        <p className="mt-1">PDF, JPG, PNG (Max 10MB)</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Manual Notes Tab Content */}
                {((!selectedNoteToEdit && createSubTab === 'manual') || selectedNoteToEdit) && (
                  <>
                    {/* Note Title */}
                    <div>
                      <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Note Title</label>
                      <Input
                        value={noteTitle}
                        onChange={(e) => setNoteTitle(e.target.value)}
                        placeholder="e.g. Photosynthesis Process"
                        className={cn("rounded-lg", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] placeholder-[hsl(var(--muted-foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}
                      />
                    </div>

                    {/* Rich Text Editor */}
                    <div>
                      <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Note Content</label>

                      {/* Formatting Toolbar */}
                      <div className={cn("rounded-t-lg border flex flex-wrap gap-1 p-2", isDark ? "bg-[hsl(var(--card-border))]/20 border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}>
                        <button
                          onClick={() => {
                            if (editor) {
                              editor.chain().focus().toggleBold().run();
                            }
                          }}
                          className={cn("p-2 rounded transition-colors", editor?.isActive('bold') ? (isDark ? "bg-[hsl(var(--gold-primary))] text-white" : "bg-[#C9A961] text-white") : (isDark ? "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/50" : "text-[#2B2B2B] hover:bg-[#E0D4BF]"))}
                          title="Bold (Ctrl+B)"
                        >
                          <Bold className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (editor) {
                              editor.chain().focus().toggleItalic().run();
                            }
                          }}
                          className={cn("p-2 rounded transition-colors", editor?.isActive('italic') ? (isDark ? "bg-[hsl(var(--gold-primary))] text-white" : "bg-[#C9A961] text-white") : (isDark ? "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/50" : "text-[#2B2B2B] hover:bg-[#E0D4BF]"))}
                          title="Italic (Ctrl+I)"
                        >
                          <Italic className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (editor) {
                              editor.chain().focus().toggleUnderline().run();
                            }
                          }}
                          className={cn("p-2 rounded transition-colors", editor?.isActive('underline') ? (isDark ? "bg-[hsl(var(--gold-primary))] text-white" : "bg-[#C9A961] text-white") : (isDark ? "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/50" : "text-[#2B2B2B] hover:bg-[#E0D4BF]"))}
                          title="Underline (Ctrl+U)"
                        >
                          <u>U</u>
                        </button>
                      </div>

                      {/* Editor */}
                      <div className={cn("rounded-b-lg border border-t-0 overflow-hidden", isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}>
                        {editor && (
                          <EditorContent
                            editor={editor}
                            className={cn("rounded-b-lg", isDark ? "bg-[hsl(var(--card-border))]/30" : "bg-white")}
                          />
                        )}
                      </div>
                      <p className={cn("text-xs mt-2", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Use the toolbar above to format your note</p>
                    </div>
                  </>
                )}

                {/* Toggles */}
                <div className="space-y-3 border-t pt-4" style={{ borderColor: isDark ? 'hsl(var(--card-border))' : '#E0D4BF' }}>
                  <div className="flex items-center justify-between">
                    <label className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Visible to Students</label>
                    <button
                      onClick={() => setVisibleToStudents(!visibleToStudents)}
                      className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", visibleToStudents ? (isDark ? "bg-[hsl(var(--gold-primary))]" : "bg-[#2B2B2B]") : "bg-[#D4C4A8]")}
                    >
                      <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", visibleToStudents ? "translate-x-6" : "translate-x-1")} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Allow Download</label>
                    <button
                      onClick={() => setAllowDownload(!allowDownload)}
                      className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", allowDownload ? (isDark ? "bg-[hsl(var(--gold-primary))]" : "bg-[#2B2B2B]") : "bg-[#D4C4A8]")}
                    >
                      <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", allowDownload ? "translate-x-6" : "translate-x-1")} />
                    </button>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-4">
                  {/* Show "Upload & Process" button for AI-OCR tab when creating new note */}
                  {(!selectedNoteToEdit && createSubTab === 'file') && (
                    <Button
                      onClick={handleFileUpload}
                      disabled={uploading || !selectedSubjectId || !selectedChapterId || !selectedFile}
                      className={cn("flex-1 shadow-md h-10 font-medium rounded-lg", (uploading || !selectedSubjectId || !selectedChapterId || !selectedFile) ? "opacity-50 cursor-not-allowed" : getButtonColor(isDark))}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload & Process
                        </>
                      )}
                    </Button>
                  )}

                  {/* Show "Save Note" button for Manual Notes tab or when editing */}
                  {((!selectedNoteToEdit && createSubTab === 'manual') || selectedNoteToEdit) && (
                    <>
                      <Button
                        onClick={handleSaveNote}
                        disabled={updatingNote || !isSaveButtonEnabled()}
                        className={cn("flex-1 shadow-md h-10 font-medium rounded-lg", (updatingNote || !isSaveButtonEnabled()) ? "opacity-50 cursor-not-allowed" : getButtonColor(isDark))}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        {selectedNoteToEdit ? (updatingNote ? 'Updating...' : 'Update Note') : 'Save Note'}
                      </Button>
                      {selectedNoteToEdit && (
                        <Button
                          onClick={handleCancelEdit}
                          className={cn("flex-1 h-10 font-medium rounded-lg shadow-md", getButtonColor(isDark))}
                        >
                          Cancel
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* SAVED NOTES TAB */}
        {activeTab === 'saved' && (
          <div className="space-y-6">
            {renderAssignmentWarning()}
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={cn("w-full pl-10 pr-4 py-2 rounded-lg border", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}
              />
              <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
            </div>

            {/* Loading State */}
            {loading.notes && (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            )}

            {/* Notes Grid */}
            {!loading.notes && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredNotes.map((note) => {
                  const processing = isNoteProcessing(note);
                  return (
                    <Card
                      key={note.id}
                      className={cn(
                        "border shadow-sm transition-all flex flex-col h-full",
                        processing
                          ? "opacity-60 cursor-not-allowed"
                          : "hover:shadow-lg cursor-pointer",
                        isDark
                          ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border-[hsl(var(--card-border))]"
                          : "bg-gradient-to-br from-[#F5F1EA] to-[#EDE3D2] border-[#E0D4BF]"
                      )}
                    >
                      <CardContent className="p-5 space-y-3 flex flex-col h-full pointer-events-none">
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex-1 flex items-start gap-2 flex-wrap">
                              <h3 className={cn("font-bold text-base line-clamp-2 flex-1", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{note.title}</h3>
                              {/* Processing Status Badge */}
                              {processing && (
                                <Badge
                                  className={cn("text-xs px-2 py-0.5 flex-shrink-0 flex items-center gap-1", isDark ? "bg-yellow-600 text-white" : "bg-yellow-500 text-white")}
                                  title={getProcessingStatusLabel(note.processing_status)}
                                >
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  {getProcessingStatusLabel(note.processing_status)}
                                </Badge>
                              )}
                              {/* AI-OCR Label */}
                              {(note.file_upload || note.file_path) && !processing && (
                                <Badge
                                  className={cn("text-xs px-2 py-0.5 flex-shrink-0", isDark ? "bg-blue-600 text-white" : "bg-blue-500 text-white")}
                                  title="AI OCR Generated"
                                >
                                  AI-OCR
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {!processing && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedNote(note);
                                    setShowPreviewModal(true);
                                  }}
                                  className={cn("p-1 rounded hover:bg-opacity-20 transition-colors pointer-events-auto", isDark ? "text-[hsl(var(--gold-primary))] hover:bg-[hsl(var(--gold-primary))]" : "text-[#C9A961] hover:bg-[#C9A961]")}
                                  title="Preview Note"
                                >
                                  <Eye className={cn("w-4 h-4 flex-shrink-0")} />
                                </button>
                              )}
                              {note.file_url && !processing && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(note.file_url, '_blank');
                                  }}
                                  className={cn("p-1 rounded hover:bg-opacity-20 transition-colors pointer-events-auto", isDark ? "text-blue-400 hover:bg-blue-400" : "text-blue-600 hover:bg-blue-600")}
                                  title="Download file"
                                >
                                  <Download className={cn("w-4 h-4 flex-shrink-0")} />
                                </button>
                              )}
                              {!processing && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteNoteClick(note.id);
                                  }}
                                  className={cn("p-1 rounded hover:bg-opacity-20 transition-colors pointer-events-auto", isDark ? "text-red-400 hover:bg-red-400" : "text-red-600 hover:bg-red-600")}
                                  title="Delete note"
                                >
                                  <Trash2 className={cn("w-4 h-4 flex-shrink-0")} />
                                </button>
                              )}
                            </div>
                          </div>
                          {processing ? (
                            <p className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Processing...</p>
                          ) : note.content || (note.structured_content && note.structured_content.sections?.length > 0) ? (
                            <div
                              className={cn(
                                "prose prose-sm max-w-none line-clamp-3",
                                "[&_a]:text-[hsl(var(--green-bright))] [&_a]:hover:text-[hsl(var(--teal-vivid))] [&_a]:underline [&_a]:underline-offset-2",
                                isDark ? "prose-invert" : "text-[#2B2B2B]"
                              )}
                            >
                              <ReactMarkdown
                                remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                                rehypePlugins={[[rehypeKatex, { throwOnError: false, strict: false, trust: true }]]}
                              >
                                {sanitizeChatMarkdown(getNoteMarkdownForDisplay(note))}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            <p className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>No content</p>
                          )}
                        </div>

                        <div className="space-y-1.5 border-t pt-3" style={{ borderColor: isDark ? 'hsl(var(--card-border))' : '#E0D4BF' }}>
                          <div className="flex items-center justify-between">
                            <span className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                              {note.subject?.subject_name || 'N/A'}
                            </span>
                          </div>
                          <p className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                            {note.chapter?.chapter_name || 'N/A'}
                          </p>
                          {/* Assigned Classes (Grade-Division) */}
                          {note.divisions && note.divisions.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {note.divisions.map((div) => (
                                <Badge
                                  key={div.id}
                                  variant="outline"
                                  className={cn("text-xs px-2 py-0.5", isDark ? "border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "border-[#E0D4BF] text-[#2B2B2B]")}
                                >
                                  Grade {div.grade?.grade_number || '?'} - {div.division_name}
                                </Badge>
                              ))}
                            </div>
                          )}
                          <p className={cn("text-xs", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                            {new Date(note.updated_at || note.created_at).toLocaleDateString()}
                          </p>
                        </div>

                        <div className="flex gap-2 pt-2 pointer-events-auto">
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!processing) {
                                // If embedding is not complete, use "Review and Save", otherwise "Edit"
                                const isReviewMode = note.embedding_status !== 'complete';
                                handleEditNote(note, isReviewMode);
                              }
                            }}
                            disabled={processing}
                            className={cn("flex-1 text-xs h-8 font-medium rounded-lg", processing ? "opacity-50 cursor-not-allowed" : getButtonColor(isDark))}
                          >
                            <Edit2 className="w-3 h-3 mr-1" />
                            {/* Show "Review and Save" if embedding is not complete, "Edit" if complete */}
                            {note.embedding_status !== 'complete' ? 'Review and Save' : 'Edit'}
                          </Button>
                          {/* Show "Assign Classes" button only when embedding is complete */}
                          {note.embedding_status === 'complete' && !processing ? (
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!processing) handleAssignClasses(note);
                              }}
                              disabled={processing}
                              className={cn("flex-1 text-xs h-8 font-medium rounded-lg", processing ? "opacity-50 cursor-not-allowed" : (isDark ? "bg-purple-600 hover:bg-purple-700 text-white" : "bg-purple-500 hover:bg-purple-600 text-white"))}
                            >
                              Assign Classes
                            </Button>
                          ) : null}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {!loading.notes && filteredNotes.length === 0 && (
              <div className={cn("text-center py-12 rounded-lg border-2 border-dashed", isDark ? "bg-[hsl(var(--card-border))]/10 border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}>
                <FileText className={cn("w-12 h-12 mx-auto mb-4 opacity-50", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                <p className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>No notes found. Create your first note!</p>
              </div>
            )}
          </div>
        )}

        {/* View Note Modal/Floating Window */}
        {viewingNote && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
            onClick={handleCloseViewNote}
          >
            <div
              className={cn("relative w-full max-w-4xl max-h-[90vh] rounded-lg shadow-xl flex flex-col", isDark ? "bg-[hsl(var(--card))] border border-[hsl(var(--card-border))]" : "bg-white border border-[#E0D4BF]")}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className={cn("flex items-center justify-between p-6 border-b", isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}>
                <div className="flex-1">
                  <h2 className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                    {viewingNote.title}
                  </h2>
                  <div className="flex items-center gap-4 mt-2">
                    <span className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                      {viewingNote.division?.division_name} • {viewingNote.subject?.subject_name} • {viewingNote.chapter?.chapter_name}
                    </span>
                    {viewingNote.visible_to_students && (
                      <Badge className={cn(isDark ? "bg-[hsl(var(--gold-primary))]" : "bg-[#C9A961]")}>
                        <Eye className="w-3 h-3 mr-1" />
                        Visible to Students
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCloseViewNote}
                  className={cn("ml-4", isDark ? "hover:bg-[hsl(var(--card-border))]" : "hover:bg-[#E0D4BF]")}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div
                  className={cn(
                    "prose max-w-none",
                    "[&_a]:text-[hsl(var(--green-bright))] [&_a]:hover:text-[hsl(var(--teal-vivid))] [&_a]:underline [&_a]:underline-offset-2",
                    isDark ? "prose-invert" : "text-[#2B2B2B]"
                  )}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                    rehypePlugins={[[rehypeKatex, { throwOnError: false, strict: false, trust: true }]]}
                  >
                    {sanitizeChatMarkdown(getNoteMarkdownForDisplay(viewingNote) || 'No content available')}
                  </ReactMarkdown>
                </div>
              </div>

              {/* Footer */}
              <div className={cn("flex items-center justify-between p-6 border-t", isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}>
                <span className={cn("text-xs", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                  Created: {new Date(viewingNote.created_at).toLocaleString()} •
                  Updated: {new Date(viewingNote.updated_at || viewingNote.created_at).toLocaleString()}
                </span>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      handleCloseViewNote();
                      handleEditNote(viewingNote);
                    }}
                    className={cn("shadow-md", getButtonColor(isDark))}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  {viewingNote.file_url && (
                    <Button
                      onClick={() => window.open(viewingNote.file_url, '_blank')}
                      className={cn("shadow-md", getButtonColor(isDark))}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download File
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assign Classes Dialog */}
        {assignClassesNote && (
          <Dialog open={!!assignClassesNote} onOpenChange={handleCloseAssignClasses}>
            <DialogContent className={cn("max-w-2xl", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
              <DialogHeader>
                <DialogTitle className={cn(isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                  Assign Note to Classes
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <p className={cn("text-sm mb-3", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                    Select classes (Grade-Division) to assign this note to:
                  </p>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {(() => {
                      // Filter classes to only show those matching the note's subject and grade
                      const noteSubjectId = assignClassesNote?.subject?.id;
                      const noteGradeId = assignClassesNote?.subject?.grade?.id ||
                        (assignClassesNote?.divisions?.[0]?.grade?.id);

                      const filteredClasses = classes.filter(cls => {
                        // Match by subject_id and grade_id
                        return cls.subject_id === noteSubjectId && cls.grade_id === noteGradeId;
                      });

                      return filteredClasses.length > 0 ? (
                        filteredClasses.map(cls => {
                          const divisionId = cls.division_id; // Use division_id, not cls.id
                          return (
                            <div
                              key={cls.id}
                              className={cn("flex items-center space-x-2 p-2 rounded hover:bg-opacity-50 cursor-pointer",
                                isDark ? "hover:bg-[hsl(var(--card-border))]" : "hover:bg-[#E0D4BF]"
                              )}
                              onClick={() => handleToggleDivision(divisionId)}
                            >
                              <Checkbox
                                checked={selectedDivisionIds.includes(divisionId)}
                                onCheckedChange={() => handleToggleDivision(divisionId)}
                                className={cn(isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}
                              />
                              <label
                                className={cn("text-sm font-medium flex-1 cursor-pointer",
                                  isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]"
                                )}
                              >
                                {cls.full_name || `${cls.subject_name} - Grade ${cls.grade_name}${cls.division_name}`}
                              </label>
                            </div>
                          );
                        })
                      ) : (
                        <p className={cn("text-sm text-center py-4", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                          No classes available for this subject and grade
                        </p>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={handleCloseAssignClasses}
                  disabled={assigningClasses}
                  className={cn(isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveAssignedClasses}
                  disabled={assigningClasses || selectedDivisionIds.length === 0}
                  className={cn("text-white", isDark ? "bg-purple-600 hover:bg-purple-700" : "bg-purple-500 hover:bg-purple-600")}
                >
                  {assigningClasses ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className={cn(isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
            <AlertDialogHeader>
              <AlertDialogTitle className={cn("text-xl font-bold", isDark ? "text-white" : "text-[#2B2B2B]")}>
                Delete Note?
              </AlertDialogTitle>
              <AlertDialogDescription className={cn("text-base", isDark ? "text-white" : "text-[#6B6B6B]")}>
                Are you sure you want to delete this note? This will permanently delete:
                <ul className="list-disc list-inside mt-3 space-y-1 text-sm">
                  <li>The note content</li>
                  <li>Associated file (if any)</li>
                  <li>Vector embeddings</li>
                  <li>All database records</li>
                </ul>
                <p className="mt-3 font-semibold text-red-600">
                  This action cannot be undone.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setNoteToDelete(null);
                }}
                className={cn("font-medium", isDark ? "bg-[hsl(var(--card-border))] text-white hover:bg-[hsl(var(--card-border))]/80 hover:text-white" : "bg-[#E0D4BF] text-[#2B2B2B] hover:bg-[#D4C4A8]")}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                className={cn("font-semibold text-white", isDark ? "bg-red-600 hover:bg-red-700" : "bg-red-600 hover:bg-red-700")}
              >
                Delete Note
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Preview Modal */}
      <TeacherNotePreviewModal
        note={selectedNote}
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        isDark={isDark}
        onRepairMath={handleRepairMathForSelectedNote}
        repairingMath={repairingMath}
      />
    </div>
  );
}
